import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, Input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { ConfirmDialogEventType, ConfirmDialogSeverity } from '../confirmDialog/confirmDialogComponent';
import { ConfirmDialogService } from '../confirmDialog/confirmDialogService';
import { FilterDateSelectValue } from '../../entities/filterDateSelect/filter-date-select-value';
import { User } from '../../entities/user/user';
import { ApolloService } from '../../services/apollo.service';
import { ToastService } from '../../services/toast/toast.service';
import * as fromGraphQl from '../notification-diagnostics/graphql';
import {
  DELIVERY_CHANNEL_VALUES,
  DELIVERY_STATUS_VALUES,
  deliveryChannelLabelKey,
  deliveryStatusLabelKey,
  FilterOption
} from '../notification-diagnostics/notification-labels';

/**
 * Server-Default. Die Obergrenze von 100 wird serverseitig still geklemmt (MAX_LIMIT).
 *
 * Filterzustand und Cursor-Mechanik hier sind bewusst eine Kopie von
 * notification-admin-list.component.ts (andere Filtertypen, andere Query-Dokumente) — eine
 * Korrektur an loadMore()/queryPage() hier zieht dort nicht automatisch mit, das muss von Hand
 * nachgezogen werden.
 */
const PAGE_SIZE = 50;

/**
 * Zustellungs-Diagnose fuer Support (#737).
 *
 * Beantwortet „warum hat der Fahrer die Benachrichtigung nicht bekommen?" ohne Datenbankzugriff.
 * Adressatenkreis ist Support, nicht der Endnutzer — technische Bezeichner (typeKey, Enum-Werte,
 * UUIDs, roher Fehlertext) sind hier erwuenscht.
 *
 * Die Cursor-Mechanik liegt bewusst NICHT im ReactiveContainer: der ersetzt seine Daten bei jeder
 * Emission, „Mehr laden" muss aber anhaengen. Vorlage ist NotificationService.loadMore() (#736).
 * nx-simple-paginator scheidet aus — sein totalElements ist input.required, und die API liefert
 * bewusst kein totalCount.
 */
@Component({
  selector: 'app-notification-delivery-list',
  templateUrl: './notification-delivery-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationDeliveryListComponent implements OnInit {

  private readonly apolloService = inject(ApolloService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  // --- Filterzustand. Bewusst nicht ueber FilterStateService persistiert: eine Diagnose-Sitzung
  // ist einmalig, ein gemerkter Filter waere beim naechsten Support-Fall eher irrefuehrend.
  readonly recipientUserId = signal<string | null>(null);
  readonly channel = signal<string | null>(null);
  readonly status = signal<string | null>(null);
  readonly typeKey = signal<string | null>(null);
  readonly dateRange = signal<FilterDateSelectValue | null>(null);

  // --- Ergebnis
  readonly items = signal<fromGraphQl.NotificationDeliveryAuditEntry[]>([]);
  private readonly nextCursor = signal<string | null>(null);
  readonly loading = signal(false);
  readonly loadingMore = signal(false);
  readonly error = signal(false);
  readonly requeueing = signal(false);
  readonly hasMore = computed(() => this.nextCursor() !== null);

  // --- Daten vom Container. Klassisches @Input mit Signal dahinter, nicht input(): die Liste wird
  // in ihrem Test ueber TestBed.inject geholt, dort gibt es kein componentRef.setInput. Kein
  // Alias: @angular-eslint/no-input-rename verbietet @Input('users') set usersInput(...), deshalb
  // traegt das Backing-Signal den "Value"-Suffix statt des Inputs.
  readonly usersValue = signal<User[]>([]);

  /** Nutzerliste fuer den Empfaengerfilter und zum Aufloesen der UUID in der Zeile. */
  @Input() set users(value: User[] | null) {
    this.usersValue.set(value ?? []);
  }

  readonly typeOptionsValue = signal<FilterOption[]>([]);

  /** Typ-Optionen aus dem Katalog. label kommt fertig uebersetzt vom Server. */
  @Input() set typeOptions(value: FilterOption[] | null) {
    this.typeOptionsValue.set(value ?? []);
  }

  /** Werte sind exakt die Enum-Namen — DeliveryStatus.valueOf(status) ist case-sensitiv. */
  readonly statusOptions: FilterOption[] = DELIVERY_STATUS_VALUES.map((value) => ({
    label: deliveryStatusLabelKey(value),
    value
  }));

  readonly channelOptions: FilterOption[] = DELIVERY_CHANNEL_VALUES.map((value) => ({
    label: deliveryChannelLabelKey(value),
    value
  }));

  /** UUID -> Anzeigename. Dieselbe Nutzerliste speist den Empfaengerfilter und die Zeile. */
  private readonly userNames = computed(() => {
    const map = new Map<string, string>();
    for (const user of this.usersValue()) {
      map.set(user.id, user.displayName);
    }
    return map;
  });

  ngOnInit(): void {
    this.loadFirstPage();
  }

  recipientName(userId: string): string | null {
    return this.userNames().get(userId) ?? null;
  }

  // --- Filteraenderungen. Jede setzt den Cursor zurueck und ersetzt die Liste.

  onRecipientChange(userId: string | null): void {
    this.recipientUserId.set(userId ?? null);
    this.loadFirstPage();
  }

  onChannelChange(channel: string | null): void {
    this.channel.set(channel ?? null);
    this.loadFirstPage();
  }

  onStatusChange(status: string | null): void {
    this.status.set(status ?? null);
    this.loadFirstPage();
  }

  onTypeKeyChange(typeKey: string | null): void {
    this.typeKey.set(typeKey ?? null);
    this.loadFirstPage();
  }

  onDateRangeChange(range: FilterDateSelectValue | null): void {
    this.dateRange.set(range ?? null);
    this.loadFirstPage();
  }

  /**
   * `filter` ist im Schema non-null: ohne gesetzten Filter geht ein leeres Objekt ueber die Leitung,
   * niemals null und niemals ein Objekt mit undefined-Werten.
   *
   * from/to kommen von nx-filter-date-select bereits als ISO-8601-Instants mit `Z`
   * (`toISOString()`), das ist genau, was `Instant.parse` serverseitig erwartet. Beide Grenzen sind
   * inklusiv und filtern auf createdAt — nicht auf sentAt.
   */
  buildFilter(): fromGraphQl.NotificationDeliveryAuditFilter {
    const filter: fromGraphQl.NotificationDeliveryAuditFilter = {};
    const recipientUserId = this.recipientUserId();
    const channel = this.channel();
    const status = this.status();
    const typeKey = this.typeKey();
    const range = this.dateRange();

    if (recipientUserId) {
      filter.recipientUserId = recipientUserId;
    }
    if (channel) {
      filter.channel = channel as fromGraphQl.NotificationChannelKey;
    }
    if (status) {
      filter.status = status;
    }
    if (typeKey) {
      filter.typeKey = typeKey;
    }
    if (range?.fromDate) {
      filter.from = range.fromDate;
    }
    if (range?.toDate) {
      filter.to = range.toDate;
    }
    return filter;
  }

  loadFirstPage(): void {
    this.loading.set(true);
    this.error.set(false);

    this.queryPage(undefined)
      .subscribe({
        next: (result) => {
          const page = result?.data?.getNotificationDeliveryAuditPage;
          this.items.set(page?.items ?? []);
          this.nextCursor.set(page?.nextCursor ?? null);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.error.set(true);
        }
      });
  }

  /**
   * Das Ende der Liste sagt ausschliesslich nextCursor === null. Nie aus einer kuerzeren Antwort als
   * angefragt darauf schliessen — der Server klemmt limit still bei 100.
   */
  loadMore(): void {
    const cursor = this.nextCursor();
    if (!cursor || this.loadingMore()) {
      return;
    }
    this.loadingMore.set(true);

    this.queryPage(cursor)
      .subscribe({
        next: (result) => {
          const page = result?.data?.getNotificationDeliveryAuditPage;
          this.items.update((items) => [...items, ...(page?.items ?? [])]);
          this.nextCursor.set(page?.nextCursor ?? null);
          this.loadingMore.set(false);
        },
        error: () => {
          // Bestehende Zeilen bleiben stehen, der Knopf bleibt aktiv fuer einen erneuten Versuch.
          this.loadingMore.set(false);
        }
      });
  }

  /**
   * Requeue.
   *
   * requeueFailedNotificationDeliveries nimmt denselben Filtertyp wie die Liste, wertet aber NUR
   * recipientUserId, channel und typeKey aus — status, from und to verwirft der Server. Wer „letzte
   * 24 Stunden, FAILED" filtert, reiht damit auch die fehlgeschlagenen Zustellungen von vor drei
   * Wochen neu ein. Der Dialog muss das benennen; den Listenfilter unkommentiert als „das wird
   * passieren" darzustellen waere eine Falschaussage.
   *
   * Bewusst NICHT an gesetzte Filter gebunden: „alle fehlgeschlagenen E-Mails erneut einreihen" ist
   * ein legitimer Fall. Stattdessen benennt der Dialog ein nicht gesetztes Kriterium als „alle".
   */
  onRequeue(): void {
    if (this.requeueing()) {
      return;
    }
    const all = this.translate.instant('core.notificationDiagnostics.requeue.all');
    const message = this.translate.instant('core.notificationDiagnostics.requeue.confirmMessage', {
      recipient: this.recipientUserId() ? (this.recipientName(this.recipientUserId()) ?? this.recipientUserId()) : all,
      channel: this.channel() ? this.translate.instant(deliveryChannelLabelKey(this.channel())) : all,
      type: this.typeKey() ?? all
    });

    const dialogRef = this.confirmDialogService.open({
      header: 'core.notificationDiagnostics.requeue.confirmHeader',
      // Fertig gerendert uebergeben: ConfirmDialogService kann keine Parameter, keine Umbrueche und
      // kein HTML — er rendert stur {{message | translate}}.
      message,
      severity: ConfirmDialogSeverity.WARN,
      confirmLabel: 'core.notificationDiagnostics.requeue.confirmLabel'
    });

    dialogRef.onClose
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((result: ConfirmDialogEventType) => {
        if (result === ConfirmDialogEventType.CONFIRM) {
          this.executeRequeue();
        }
      });
  }

  private executeRequeue(): void {
    this.requeueing.set(true);

    // mutateObservable mit { next, error }: mutate() und mutateAndExecute() subscriben ohne
    // error-Callback und verschlucken den Fehler (apollo.service.ts:11-35).
    this.apolloService
      .mutateObservable<fromGraphQl.RequeueResponse>(fromGraphQl.REQUEUE_FAILED_DELIVERIES_MUTATION, {
        filter: this.buildFilter()
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          const count = result?.data?.requeueFailedNotificationDeliveries ?? 0;
          this.toastService.addToastWithMessage(
            this.translate.instant('core.notificationDiagnostics.requeue.successToastMessage', { count })
          );
          this.requeueing.set(false);
          // Der Poller-Hint ist gesetzt, die Zustellung startet in etwa einer Sekunde. Neu laden,
          // damit die Zeilen von FAILED auf PENDING umspringen.
          this.loadFirstPage();
        },
        error: () => {
          this.toastService.addErrorToast('core.notificationDiagnostics.requeue.errorToastMessage');
          this.requeueing.set(false);
        }
      });
  }

  /**
   * fetchPolicy network-only ist hier Pflicht: der Default von apollo.query ist cache-first (die
   * network-only-Voreinstellung in graphql.module.ts gilt nur fuer watchQuery). Nach einem Requeue
   * zeigte die Liste sonst weiter FAILED aus dem Cache.
   */
  private queryPage(cursor: string | undefined) {
    return this.apolloService
      .query<fromGraphQl.DeliveryAuditPageResponse>(
        fromGraphQl.DELIVERY_AUDIT_PAGE_QUERY,
        { filter: this.buildFilter(), cursor, limit: PAGE_SIZE },
        { fetchPolicy: 'network-only' }
      )
      .pipe(take(1), takeUntilDestroyed(this.destroyRef));
  }

}
